import React, { useCallback, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Outlet,
  useLocation,
  matchPath,
  useNavigate,
  useParams,
} from 'react-router-dom';
import config from 'app-config';
import AddWorklogIcon from '@chronos/components/Icons/AddWorklogIcon';
import ExternalLinkIcon from '@chronos/components/Icons/ExternalLinkIcon';
import HamburgerIcon from '@chronos/components/Icons/HamburgerIcon';
import CrossIcon from '@chronos/components/Icons/CrossIcon';
import chronosApi from '@chronos/api-client/chronosApi';
import socketHelper from '@chronos/utils/socketHelper';
import {
  actions,
  selectSidebarIsCollapsed,
  selectHeaderTitle,
  selectLiveWorklogs,
  setIsInitialized,
  selectIsInitialized,
  selectLiveWorklogsByType,
  initialize,
  setLiveWorklogs,
} from '@chronos/slices/boardSlice';
import { useGetAuthUserQuery } from '@chronos/slices/usersApi';
import {
  initialBoardsState,
  useGetBoardsQuery,
} from '@chronos/slices/boardsApi';
import eventEmitter from '@chronos/utils/eventEmitter';
import { getLiveWorklogId } from '@chronos/utils';
import {
  useGetTrelloBoardCardsQuery,
  useTrelloCards,
  initialCardState,
} from '@chronos/slices/cardsApi';
import { useGetTrelloBoardAdminSettingsQuery } from '@chronos/slices/settingsApi';
import { BoardContextWrapper } from '../../contexts/BoardContextWrapper';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import InstallPrompt from '../../components/InstallPrompt';
import {
  closeModal,
  openModal,
  selectModalState,
} from '../../slices/modalSlice';
import TrackingBar from '../TrackingBar';
import { clearState } from '../../utils/state-saver';
import { STATE_WORKLOGS_KEY, trelloCardFields } from '../../constants';
import {
  selectShowInstallPrompt,
  setTheme,
  selectTheme,
} from '../../slices/appSlice';
import WorklogFormModal from '../WorklogForm/Modal';
import * as S from './styled';

const getInitialTrackerExpanded = () => {
  let isExpanded = true;
  try {
    isExpanded =
      (localStorage.getItem('isTrackerExpanded') ?? 'true') === 'true';
  } catch (e) {
    console.error(e);
  }
  return isExpanded;
};

export const mapState = (state) => ({
  sidebarIsCollapsed: selectSidebarIsCollapsed(state),
  headerTitle: selectHeaderTitle(state),
  liveWorklogs: selectLiveWorklogs(state),
  showInstallPrompt: selectShowInstallPrompt(state),
});

const Board = (props) => {
  const dispatch = useDispatch();
  const { boardId: currentBoardId } = useParams();
  const { showInstallPrompt, sidebarIsCollapsed, headerTitle } =
    useSelector(mapState);
  const isInitialized = useSelector(selectIsInitialized);
  const currentTheme = useSelector(selectTheme);
  const modalState = useSelector(selectModalState);

  const location = useLocation();
  const navigate = useNavigate();

  const matches = matchPath('/:boardId/cards/:cardId', location.pathname);
  let { cardId: routeCardId } = useParams();
  if (!routeCardId && matches?.params.cardId) {
    routeCardId = matches.params.cardId;
  }

  const { data: profile } = useGetAuthUserQuery(null);
  const {
    data: { ids: boardsIds, entities: boardsEntities } = initialBoardsState,
  } = useGetBoardsQuery({ memberId: profile.id });
  const { data: boardAdminSettings } = useGetTrelloBoardAdminSettingsQuery(
    {
      boardId: currentBoardId,
    },
    { skip: !isInitialized }
  );

  const { data: { entities: cardsEntities } = initialCardState } =
    useGetTrelloBoardCardsQuery({
      boardId: currentBoardId,
      filter: 'visible',
      fields: trelloCardFields,
    });
  const routeCard = cardsEntities[routeCardId];
  const [fetchOutTrelloCards] = useTrelloCards({
    boardId: currentBoardId,
    filter: 'visible',
    fields: trelloCardFields,
  });

  const currentBoard = boardsEntities[currentBoardId];
  const organizationId = currentBoard.idOrganization;

  const ownLiveWorklogs = useSelector((state) =>
    selectLiveWorklogsByType(state, { roomType: 'member' })
  );
  const boardLiveWorklogs = useSelector((state) =>
    selectLiveWorklogsByType(state, { roomType: 'board' })
  );
  const boardLiveWorklog = useMemo(
    () => ownLiveWorklogs.find((lw) => lw.boardId === currentBoardId),
    [ownLiveWorklogs, currentBoardId]
  );

  const onLiveWorklogs = useCallback(
    (data) => {
      log.debug('[socket]:', '🫀', 'liveWorklogs', data);
      const { roomType } = data;
      const liveWorklogs =
        data.roomType === 'member' ? ownLiveWorklogs : boardLiveWorklogs;

      if (data.action === 'sync') {
        dispatch(
          setLiveWorklogs({ roomType, liveWorklogs: data.liveWorklogs })
        );
      }
      if (data.action === 'set') {
        const index = liveWorklogs.findIndex(
          (worklog) =>
            getLiveWorklogId(worklog) === getLiveWorklogId(data.liveWorklog)
        );
        const updatedLiveWorklogs = [...liveWorklogs];
        if (index !== -1) {
          updatedLiveWorklogs[index] = data.liveWorklog;
        } else {
          updatedLiveWorklogs.push(data.liveWorklog);
        }
        dispatch(
          setLiveWorklogs({
            roomType,
            liveWorklogs: updatedLiveWorklogs,
          })
        );
      }
      if (data.action === 'delete') {
        dispatch(
          setLiveWorklogs({
            roomType,
            liveWorklogs: liveWorklogs.filter(
              (worklog) =>
                getLiveWorklogId(worklog) !== getLiveWorklogId(data.liveWorklog)
            ),
          })
        );
      }
    },
    [ownLiveWorklogs, boardLiveWorklogs]
  );

  useEffect(() => {
    const chronosJWT = chronosApi.getJWT();
    if (!isInitialized) {
      // skip initialize
      return () => {};
    }
    // need to connect to socket when boardId is changed and when app is initialized
    socketHelper.initialize({
      chronosJWT,
      socketUrl: config.chronosSocketUrl,
    });
    socketHelper.connect({
      boardId: currentBoardId,
      memberId: profile.id,
    });

    socketHelper.getSocket().on('updateState', () => {
      log.info('[socket]:', 'updateState');
      eventEmitter.emit('updateState');
    });

    socketHelper.getSocket().on('liveWorklogs', (socketMessage) => {
      eventEmitter.emit('liveWorklogs', socketMessage);
    });
    return () => {
      socketHelper.disconnect();
    };
  }, [isInitialized, currentBoardId, profile.id]);

  useEffect(() => {
    eventEmitter.on('liveWorklogs', onLiveWorklogs);
    return () => {
      eventEmitter.off('liveWorklogs', onLiveWorklogs);
    };
  }, [ownLiveWorklogs, boardLiveWorklogs]);

  useEffect(() => {
    // if current board is changed then reset state & reconnect socket with new initialize()
    // TODO: run initialize if boardId has been changed
    if (isInitialized) {
      return;
    }
    (async () => {
      const data = {
        boardId: currentBoardId,
        organizationId,
        userId: profile.id,
        trelloIdPlugin: config.trelloIdPlugin,
        socketUrl: config.chronosSocketUrl,
      };
      await dispatch(initialize(data));
      dispatch(setIsInitialized(true));
    })();
  }, [dispatch, currentBoardId, profile.id, organizationId]);

  // useEffect(() => {
  //   if (currentBoardId) {
  //     dispatch(actions.resetState());
  //     socketHelper.disconnect();
  //     clearState(STATE_WORKLOGS_KEY);
  //     initialize();
  //   }j
  // }, [currentBoardId]);

  const handleChangeBoard = useCallback(
    (id) => {
      dispatch(actions.resetState());

      clearState(STATE_WORKLOGS_KEY);

      navigate(`/${id}`);
    },
    [currentBoardId]
  );

  const [isTrackerExpanded, setTrackerExpanded] = useState(
    getInitialTrackerExpanded() && boardAdminSettings?.allowMultitracking
  );

  const visibleLiveWorklogs = useMemo(() => {
    const routeLiveWorklog = {
      boardId: currentBoardId,
      cardId: routeCardId,
      memberId: profile.id,
      startTime: Date.now(),
      pauseTrackedSeconds: 0,
      timerStatus: 'stop',
      trackingWorklogComment: '',
    };
    if (!isTrackerExpanded && boardLiveWorklog) {
      return [boardLiveWorklog];
    }
    if (!isTrackerExpanded && routeCardId) {
      return [routeLiveWorklog];
    }
    if (!boardAdminSettings?.allowMultitracking) {
      return [];
    }

    const liveWorklogs = ownLiveWorklogs.filter(
      (lw, i) =>
        lw.timerStatus !== 'stop' &&
        (i === ownLiveWorklogs.length - 1 || isTrackerExpanded)
    );
    if (!boardLiveWorklog && routeCardId) {
      liveWorklogs.push(routeLiveWorklog);
    }
    return liveWorklogs.sort((a) => {
      if (a.boardId === currentBoardId) {
        return 1;
      }
      return -1;
    });
  }, [
    currentBoardId,
    routeCardId,
    ownLiveWorklogs,
    boardLiveWorklog,
    isTrackerExpanded,
  ]);

  const openWorklogFormModal = useCallback(() => {
    dispatch(
      openModal({
        name: 'WorklogFormModal',
        meta: { title: 'Add worklog', mode: 'addWorklog' },
      })
    );
  }, [dispatch]);

  const openTrelloCardExternal = useCallback(() => {
    window.open(routeCard.shortUrl, '_blank', 'noopener,noreferrer');
  }, [routeCard]);

  const openTrelloBoardExternal = useCallback(() => {
    window.open(currentBoard.shortUrl, '_blank', 'noopener,noreferrer');
  }, [currentBoard]);

  const toggleSidebar = useCallback(() => {
    dispatch(actions.setSidebarIsCollapsed(!sidebarIsCollapsed));
  }, [dispatch, sidebarIsCollapsed]);

  const hideSidebar = useCallback(() => {
    dispatch(actions.setSidebarIsCollapsed(true));
  }, [dispatch]);

  const showSidebar = useCallback(() => {
    dispatch(actions.setSidebarIsCollapsed(false));
  }, [dispatch]);

  const handleLogout = useCallback(() => {
    socketHelper.disconnect();
    clearState(STATE_WORKLOGS_KEY);
    navigate('/#logout=true');
  }, []);

  const handleChangeTheme = useCallback(() => {
    dispatch(setTheme(currentTheme === 'light' ? 'dark' : 'light'));
    hideSidebar();
  }, [currentTheme]);

  const handleGoBack = useCallback(() => {
    navigate(`/${currentBoardId}`);
    // navigate(-1); // navigate to dashboard or cards
  }, [currentBoardId]);

  const headerIcons = useMemo(() => {
    if (routeCardId) {
      return [
        {
          name: 'hamburger',
          Icon: HamburgerIcon,
          size: ['24px', '24px'],
          callback: showSidebar,
          placement: 'left',
        },
        {
          name: 'worklog',
          Icon: AddWorklogIcon,
          size: ['24px', '24px'],
          callback: openWorklogFormModal,
          placement: 'right',
        },
        {
          name: 'externalLink',
          Icon: ExternalLinkIcon,
          size: ['24px', '24px'],
          callback: openTrelloCardExternal,
          placement: 'right',
        },
        {
          name: 'close',
          Icon: CrossIcon,
          size: ['24px', '24px'],
          callback: handleGoBack,
          placement: 'right',
        },
      ];
    }
    return [
      {
        name: 'hamburger',
        Icon: HamburgerIcon,
        size: ['24px', '24px'],
        callback: toggleSidebar,
        placement: 'left',
      },
      {
        name: 'externalLink',
        Icon: ExternalLinkIcon,
        size: ['24px', '24px'],
        callback: openTrelloBoardExternal,
        placement: 'right',
      },
    ];
  }, [
    routeCardId,
    showSidebar,
    openWorklogFormModal,
    openTrelloCardExternal,
    openTrelloBoardExternal,
    handleGoBack,
    toggleSidebar,
  ]);

  const handleWorklogFormModalClose = useCallback(
    (status) => {
      if (boardLiveWorklog?.timerStatus === 'pause' && status === 'cancel') {
        eventEmitter.emit('timer:play', boardLiveWorklog);
      }
      dispatch(closeModal());
    },
    [dispatch, boardLiveWorklog]
  );

  const hasMultipleTimers = useMemo(() => {
    const theSameCard = ownLiveWorklogs.find((lw) => lw.cardId === routeCardId);
    const { length } = ownLiveWorklogs;
    if (length > 1) return true;

    if (length === 1 && routeCardId && boardLiveWorklog) return false;

    if (length === 1 && routeCardId && !theSameCard) return true;

    return false;
    // return length > 1 || (length === 1 && !!routeCardId && !theSameCard);
  }, [ownLiveWorklogs, boardLiveWorklog, routeCardId]);

  const className = sidebarIsCollapsed ? 'collapsed' : 'expanded';
  const outletElement = <Outlet />;

  return (
    <BoardContextWrapper
      isInitialized={isInitialized}
      boardId={currentBoardId}
      organizationId={organizationId}
    >
      <S.Board className={className}>
        <Sidebar
          hideSidebar={hideSidebar}
          onChangeBoard={handleChangeBoard}
          onChangeTheme={handleChangeTheme}
          theme={currentTheme}
          onLogout={handleLogout}
          boards={boardsIds.map((id) => boardsEntities[id])}
          boardId={currentBoardId}
          liveWorklogs={ownLiveWorklogs}
        />
        <S.Container>
          {showInstallPrompt && <InstallPrompt />}
          <Header
            title={props.isHeaderTitleHidden ? '' : headerTitle}
            toggleMenu={toggleSidebar}
            icons={headerIcons}
            collapsed={sidebarIsCollapsed}
          />
          <S.Body>{outletElement}</S.Body>
          {props.isTrackingBarShown &&
            visibleLiveWorklogs.map((lw, i) => (
              <TrackingBar
                key={getLiveWorklogId(lw)}
                liveWorklog={lw}
                cardsEntities={cardsEntities}
                fetchOutTrelloCards={fetchOutTrelloCards}
                /* showEstimateModal={setEstimateCardId} */
                /* optimisticUpdateLiveWorklog={optimisticUpdateLiveWorklog} */
                setTrackerExpanded={setTrackerExpanded}
                hasMultipleTimers={
                  boardAdminSettings?.allowMultitracking && hasMultipleTimers
                }
                isTrackerExpanded={isTrackerExpanded}
                isExpandCollapseVisible={
                  isTrackerExpanded
                    ? i === visibleLiveWorklogs.length - 1
                    : i === 0
                }
              />
            ))}
          {!props.isTrackingBarShown &&
            modalState.isOpen &&
            modalState.name === 'WorklogFormModal' && (
              <WorklogFormModal
                isOpen
                onClose={handleWorklogFormModalClose}
                cardId={routeCardId}
                liveWorklogs={boardLiveWorklogs}
                {...modalState.meta}
              />
            )}
        </S.Container>
      </S.Board>
    </BoardContextWrapper>
  );
};

Board.defaultProps = {
  isTrackingBarShown: true,
  isHeaderTitleHidden: false,
};

Board.propTypes = {
  children: PropTypes.node,
  isTrackingBarShown: PropTypes.bool,
  isHeaderTitleHidden: PropTypes.bool,
};

export default Board;
